export interface DietaryInfo {
  isVegetarian: boolean;
  isVegan: boolean;
  isGlutenFree: boolean;
  isLactoseFree: boolean;
  isHalal: boolean;
  isKosher: boolean;
  isBio: boolean;
  isFairTrade: boolean;
  isSeasonal: boolean;
  isFastingOrtodox: boolean;
  isFastingMuslim: boolean;
  isSpicy: boolean;
  isSweet: boolean;
  isSour: boolean;
  isSalty: boolean;
  isBitter: boolean;
}

export interface Meal {
  id: number;
  name?: string;
  description?: string;
  price: number;
  imagePath?: string[];
  catererId: number;
  isApproved: boolean;
}

export interface BusinessDay {
  date: string;
  dayName: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  organisationId: number;
  isDeleted: number;
}

export interface Shift {
  id: number;
  name: string;
  description?: string;
  orderCutoffTimeOffsetDays: number;
  orderDeliveryTime: string;
  orderDeliveryDateTime: Date;
  organisationId: number;
  isApproved: boolean;
}

export interface Organisation {
  id: number;
  name: string;
  taxNumber: string;
  city?: string;
  street?: string;
  zipCode?: string;
  country?: string;
  description?: string;
  logoPath?: string;
  catererId: number;
  isApproved: boolean;
}

export interface Comment {
  id: number;
  text: string;
  organisationMenuId: number;
  menuVersion: number;
}

export enum MenuStatus {
  Draft = 'Draft',
  Submitted = 'Submitted',
  UpdateRequested = 'UpdateRequested',
  Updated = 'Updated',
  Approved = 'Approved',
  Rejected = 'Rejected',
  Active = 'Active',
  Inactive = 'Inactive',
}

export interface OrganisationMenu {
  id: number;
  name?: string;
  description?: string;
  meals: Meal[];
  organisationId: number;
  validFrom: Date | string;
  validTo: Date | string;
  status: MenuStatus;
  version: number;
  comment: string;
  comments: Comment[];
}

export interface Order {
  id: number;
  orderDate: string;
  deliveryDate: string;
  price?: number;
  privateOrder: boolean;
  isPaid: boolean;
  mealId: number;
  meal?: Meal;
  userId: number;
  user?: User;
}

export interface IOrganisationService {
  // The endpoints that actually exist in the backend according to OrganisationEndpoints.cs
  getShifts(): Promise<Shift[]>;
  addShift(shift: Partial<Shift>): Promise<Shift>;
  updateShift(shift: Shift): Promise<boolean>;
  deleteShift(id: number): Promise<boolean>;

  getComments(menuId: number): Promise<Comment[]>;
  addComment(comment: Comment): Promise<boolean>;

  getOrders(): Promise<Order[]>;
  addOrder(order: Order): Promise<Order>;
  updateOrder(order: Order): Promise<boolean>;
  deleteOrder(id: number): Promise<boolean>;

  getOrganisationMenus(): Promise<OrganisationMenu[]>;

  getUsers(): Promise<User[]>;
  addUser(user: Partial<User>): Promise<User>;
  updateUser(user: User): Promise<boolean>;
  deleteUser(id: number): Promise<boolean>;

  // Additional methods based on the UserAPI endpoints
  getMeals(): Promise<Meal[]>;
  getMealById(id: number): Promise<Meal | null>;
  getNextBusinessDays(count: number): Promise<BusinessDay[]>;
  getMealsByDate(count: number): Promise<{ [date: string]: Meal[] }>;

  // New methods
  getMenus(): Promise<OrganisationMenu[]>;
  createMenu(menu: Partial<OrganisationMenu>): Promise<OrganisationMenu>;
  updateMenu(menu: Partial<OrganisationMenu>): Promise<boolean>;
  updateMenuStatus(
    menu: OrganisationMenu,
    statusComment?: string
  ): Promise<boolean>;
  deleteMenu(id: number): Promise<boolean>;

  // Organisation profile methods
  getOrganisation(id: number): Promise<Organisation | null>;
  updateOrganisation(organisation: Organisation): Promise<boolean>;
}
